import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import type { ElementPropertiesPanelRef } from "./ElementPropertiesPanel";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Button } from "../ui/Button";
import { Heading } from "../ui/Heading";
import { Modal } from "../ui/Modal";
import { DesignElement } from "./types";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import ComponentPalette from "./ComponentPalette";
import LayerTree from "./LayerTree";
import ElementPropertiesPanel from "./ElementPropertiesPanel";
import ThreeDDesignCanvas, { ThreeDDesignCanvasRef } from "./ThreeDDesignCanvas";
import { FaSave, FaTimes, FaExclamationCircle, FaCube } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../context/ApiContext";

export interface ThreeDPrintDesignerProps {
  productId: string;
  onSave?: (customizationId: string) => void;
  onCancel?: () => void;
  initialElements?: DesignElement[];
  initialBackgroundColor?: string;
  initialBackgroundDepth?: number;
  initialBackgroundForm?: "circular" | "rectangular" | "heart" | "auto";
  draftCustomizationId?: string;
  onDraftSaved?: (customizationId: string) => void;
  onCustomizationUpdated?: (customizationId: string) => void;
}

export const ThreeDPrintDesigner: React.FC<ThreeDPrintDesignerProps> = ({
  productId,
  onSave,
  onCancel,
  initialElements = [],
  initialBackgroundColor = "#FFFFFF",
  initialBackgroundDepth = 5,
  initialBackgroundForm = "auto",
  draftCustomizationId,
  onDraftSaved,
  onCustomizationUpdated,
}) => {
  const { user } = useAuth();
  const { api } = useApi();
  const prevInitialElementsKeyRef = useRef<string>("");
  const draftLoadedRef = useRef<string | null>(null);
  const baselineInitializedRef = useRef<boolean>(false);
  const [availableColors, setAvailableColors] = useState<string[] | undefined>(undefined);

  const transformElements = useCallback((els: DesignElement[]): DesignElement[] => {
    return els.map((el, index) => ({
      ...el,
      id: el.id || `element-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
    }));
  }, []);

  const [elements, setElements] = useState<DesignElement[]>(() => {
    return transformElements(initialElements);
  });
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [selectedCanvas, setSelectedCanvas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string>(initialBackgroundColor);
  const [backgroundDepth, setBackgroundDepth] = useState<number>(initialBackgroundDepth);
  const [backgroundForm, setBackgroundForm] = useState<"circular" | "rectangular" | "heart" | "auto">(initialBackgroundForm);
  const [backgroundEnabled, setBackgroundEnabled] = useState<boolean>(true);
  const [backgroundWidth, setBackgroundWidth] = useState<number>(100);
  const [backgroundHeight, setBackgroundHeight] = useState<number>(60);
  const [backgroundBorderRadius, setBackgroundBorderRadius] = useState<number>(0);
  const [keychainRingEnabled, setKeychainRingEnabled] = useState<boolean>(false);
  const [keychainRingSide, setKeychainRingSide] = useState<"top" | "bottom" | "left" | "right">("top");
  const [keychainRingEmbeddingDepth, setKeychainRingEmbeddingDepth] = useState<number>(0.5);
  const [keychainRingSize, setKeychainRingSize] = useState<number>(1.0);
  const [keychainRingThickness, setKeychainRingThickness] = useState<number>(1.0);
  const [activeTab, setActiveTab] = useState<"components" | "layers">("components");
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const canvasRef = useRef<ThreeDDesignCanvasRef>(null);
  const propertiesPanelRef = useRef<ElementPropertiesPanelRef>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [exporting, setExporting] = useState(false);

  const baselineStateRef = useRef<{
    elements: Array<Omit<DesignElement, "id">>;
    backgroundColor: string;
    backgroundDepth: number;
    backgroundForm: "circular" | "rectangular" | "heart" | "auto";
    keychainRingEnabled: boolean;
    keychainRingSide: "top" | "bottom" | "left" | "right";
    keychainRingEmbeddingDepth: number;
    keychainRingSize: number;
    keychainRingThickness: number;
  } | null>(null);

  const initialElementsForComparison = useMemo(() => {
    return initialElements.map(({ id, ...el }) => el);
  }, [initialElements]);

  // Fetch product data to get availableColors
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${productId}`);
        if (response.data?.availableColors && Array.isArray(response.data.availableColors) && response.data.availableColors.length > 0) {
          setAvailableColors(response.data.availableColors);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        // Continue without availableColors - will use full color picker
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, api]);

  const loadDraftCustomization = useCallback(async () => {
    if (!draftCustomizationId) return;
    
    if (draftLoadedRef.current === draftCustomizationId) return;
    
    try {
      draftLoadedRef.current = draftCustomizationId;
      const customization = await productCustomizationApi.getCustomizationById(draftCustomizationId);
      if (customization) {
        let transformed: DesignElement[] = [];
        if (customization.elements && Array.isArray(customization.elements)) {
          transformed = transformElements(customization.elements as DesignElement[]);
          setElements(transformed);
        }
        
        if (customization.backgroundColor) {
          setBackgroundColor(customization.backgroundColor);
        }
        
        // Load 3D-specific background properties from threeDData
        if (customization.threeDData) {
          const threeDData = customization.threeDData as any;
          if (threeDData.backgroundDepth !== undefined) {
            setBackgroundDepth(threeDData.backgroundDepth);
          }
          if (threeDData.backgroundForm) {
            setBackgroundForm(threeDData.backgroundForm);
          }
          // Handle enabled flag - check for both boolean false and undefined
          if (threeDData.enabled !== undefined && threeDData.enabled !== null) {
            setBackgroundEnabled(Boolean(threeDData.enabled));
          } else {
            // Default to enabled if not specified (for backward compatibility)
            setBackgroundEnabled(true);
          }
          if (threeDData.width !== undefined) {
            setBackgroundWidth(threeDData.width);
          } else {
            setBackgroundWidth(100);
          }
          if (threeDData.height !== undefined) {
            setBackgroundHeight(threeDData.height);
          } else {
            setBackgroundHeight(60);
          }
          if (threeDData.borderRadius !== undefined) {
            setBackgroundBorderRadius(threeDData.borderRadius);
          } else {
            setBackgroundBorderRadius(0);
          }
          if (threeDData.keychainRingEnabled !== undefined) {
            setKeychainRingEnabled(Boolean(threeDData.keychainRingEnabled));
          } else {
            setKeychainRingEnabled(false);
          }
          if (threeDData.keychainRingSide) {
            setKeychainRingSide(threeDData.keychainRingSide);
          } else {
            setKeychainRingSide("top");
          }
          if (threeDData.keychainRingEmbeddingDepth !== undefined) {
            setKeychainRingEmbeddingDepth(threeDData.keychainRingEmbeddingDepth);
          } else {
            setKeychainRingEmbeddingDepth(0.5);
          }
          if (threeDData.keychainRingSize !== undefined) {
            setKeychainRingSize(threeDData.keychainRingSize);
          } else {
            setKeychainRingSize(1.0);
          }
          if (threeDData.keychainRingThickness !== undefined) {
            setKeychainRingThickness(threeDData.keychainRingThickness);
          } else {
            setKeychainRingThickness(1.0);
          }
        } else {
          // No threeDData - set defaults
          setBackgroundEnabled(true);
          setBackgroundWidth(100);
          setBackgroundHeight(60);
          setBackgroundBorderRadius(0);
          setKeychainRingEnabled(false);
          setKeychainRingSide("top");
          setKeychainRingEmbeddingDepth(0.5);
          setKeychainRingSize(1.0);
          setKeychainRingThickness(1.0);
        }
        
        // Don't set baseline here - it will be set in useEffect after state updates complete
        baselineInitializedRef.current = false;
      }
    } catch (error) {
      console.error("Error loading draft customization:", error);
      draftLoadedRef.current = null;
    }
  }, [draftCustomizationId, transformElements, initialBackgroundColor, initialBackgroundDepth, initialBackgroundForm]);

  useEffect(() => {
    if (draftCustomizationId) {
      if (draftLoadedRef.current !== draftCustomizationId) {
        draftLoadedRef.current = null;
      }
      loadDraftCustomization();
    } else {
      draftLoadedRef.current = null;
    }
  }, [draftCustomizationId, loadDraftCustomization]);

  // Set baseline after draft is loaded and state has been updated
  // This ensures the baseline matches the loaded state values
  useEffect(() => {
    if (!draftCustomizationId) return; // Only for drafts
    if (!draftLoadedRef.current) return; // Draft not loaded yet
    if (baselineInitializedRef.current) return; // Already initialized
    
    // Use requestAnimationFrame to ensure this runs after all state updates are processed
    requestAnimationFrame(() => {
      if (!draftCustomizationId || !draftLoadedRef.current || baselineInitializedRef.current) return;
      
      // Set baseline to match current state values after draft loading
      baselineStateRef.current = {
        elements: elements.map(({ id, ...el }) => el),
        backgroundColor,
        backgroundDepth,
        backgroundForm,
        keychainRingEnabled,
        keychainRingSide,
        keychainRingEmbeddingDepth,
        keychainRingSize,
        keychainRingThickness,
      };
      baselineInitializedRef.current = true;
    });
  }, [draftCustomizationId, elements, backgroundColor, backgroundDepth, backgroundForm, keychainRingEnabled, keychainRingSide, keychainRingEmbeddingDepth, keychainRingSize, keychainRingThickness]);

  // Initialize baseline state to match current state when there's no draft
  // This ensures no false "unsaved changes" on initial load
  useEffect(() => {
    if (draftCustomizationId) {
      baselineInitializedRef.current = false; // Reset when draft changes
      return; // Skip if loading a draft - baseline will be set in the effect above
    }
    if (baselineInitializedRef.current) return; // Already initialized
    
    // Set baseline to match current state values
    // This runs once after mount when there's no draft
    baselineStateRef.current = {
      elements: elements.map(({ id, ...el }) => el),
      backgroundColor,
      backgroundDepth,
      backgroundForm,
      keychainRingEnabled,
      keychainRingSide,
      keychainRingEmbeddingDepth,
      keychainRingSize,
      keychainRingThickness,
    };
    baselineInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftCustomizationId]); // Only re-run when draftCustomizationId changes

  useEffect(() => {
    if (draftCustomizationId) return;
    
    const currentKey = JSON.stringify(initialElementsForComparison);
    if (prevInitialElementsKeyRef.current !== currentKey) {
      const transformed = transformElements(initialElements);
      setElements(transformed);
      prevInitialElementsKeyRef.current = currentKey;
    }
  }, [initialElements, initialElementsForComparison, transformElements, draftCustomizationId]);

  useEffect(() => {
    if (!baselineStateRef.current || !baselineInitializedRef.current) {
      setHasUnsavedChanges(false);
      return;
    }

    const baseline = baselineStateRef.current;

    const elementsChanged = 
      elements.length !== baseline.elements.length ||
      elements.some((el, idx) => {
        const baselineEl = baseline.elements[idx];
        if (!baselineEl) return true;
        return (
          el.type !== baselineEl.type ||
          el.content !== baselineEl.content ||
          Math.abs(el.position.x - baselineEl.position.x) > 0.1 ||
          Math.abs(el.position.y - baselineEl.position.y) > 0.1 ||
          Math.abs(el.size.width - baselineEl.size.width) > 0.1 ||
          Math.abs(el.size.height - baselineEl.size.height) > 0.1 ||
          JSON.stringify(el.style) !== JSON.stringify(baselineEl.style) ||
          el.zIndex !== baselineEl.zIndex
        );
      });
    
    const backgroundChanged = 
      backgroundColor !== baseline.backgroundColor ||
      backgroundDepth !== baseline.backgroundDepth ||
      backgroundForm !== baseline.backgroundForm ||
      keychainRingEnabled !== baseline.keychainRingEnabled ||
      keychainRingSide !== baseline.keychainRingSide ||
      Math.abs((keychainRingEmbeddingDepth ?? 0.5) - baseline.keychainRingEmbeddingDepth) > 0.01 ||
      Math.abs((keychainRingSize ?? 1.0) - baseline.keychainRingSize) > 0.01 ||
      Math.abs((keychainRingThickness ?? 1.0) - baseline.keychainRingThickness) > 0.01;
    
    const hasChanges = elementsChanged || backgroundChanged;
    setHasUnsavedChanges(hasChanges);
  }, [elements, backgroundColor, backgroundDepth, backgroundForm, keychainRingEnabled, keychainRingSide, keychainRingEmbeddingDepth, keychainRingSize, keychainRingThickness]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Only allow text elements for 3D designer
  const addElement = useCallback((type: "text" | "shape" | "frame", content?: string) => {
    if (type !== "text") {
      return; // Only text elements allowed in 3D designer
    }

    const newElement: DesignElement = {
      id: `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: "text",
      content: content || "New Text",
      position: { x: 0, y: 0 },
      size: {
        width: 200,
        height: 50,
      },
      style: {
        fontFamily: "Arial",
        fontSize: 24,
        color: "#000000",
        borderRadius: 0,
        rotation: 0,
        opacity: 1,
        textDepth: 5, // Default depth for 3D text
        textOrientation: "flat", // Default orientation
      },
      zIndex: elements.length,
    };

    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
    setSelectedCanvas(false);
  }, [elements]);

  const updateElement = useCallback((id: string, updates: Partial<DesignElement>) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
  }, []);

  const deleteElement = useCallback((id: string) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedElement === id) {
      setSelectedElement(null);
    }
  }, [selectedElement]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) {
      return;
    }

    if (over.id === "canvas-drop-zone") {
      const dragData = active.data.current;
      if (dragData?.type === "text") {
        // For 3D designer, add element at default position
        // User can reposition it in the 3D view
        addElement("text", dragData.content);
      }
      return;
    }

    const oldIndex = elements.findIndex((el) => el.id === active.id);
    const newIndex = elements.findIndex((el) => el.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newElements = arrayMove(elements, oldIndex, newIndex);
      const updatedElements = newElements.map((el, index) => ({
        ...el,
        zIndex: index,
      }));
      setElements(updatedElements);
    }
  }, [elements, addElement]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const saveDraft = useCallback(async () => {
    try {
      const customizationData = {
        productId,
        customizationType: "3d-print" as const,
        elements: elements.map((el) => ({
          type: el.type,
          content: el.content,
          position: el.position,
          size: el.size,
          style: el.style,
          zIndex: el.zIndex,
        })),
        backgroundColor,
        threeDData: {
          backgroundDepth,
          backgroundForm,
          enabled: backgroundEnabled,
          width: backgroundWidth,
          height: backgroundHeight,
          borderRadius: backgroundBorderRadius,
          keychainRingEnabled,
          keychainRingSide,
          keychainRingEmbeddingDepth,
          keychainRingSize,
          keychainRingThickness,
        },
        isDraft: true,
      };

      let customization;
      if (draftCustomizationId) {
        customization = await productCustomizationApi.update(draftCustomizationId, customizationData);
      } else {
        customization = await productCustomizationApi.create(customizationData);
      }

      baselineStateRef.current = {
        elements: elements.map(({ id, ...el }) => el),
        backgroundColor,
        backgroundDepth,
        backgroundForm,
        keychainRingEnabled,
        keychainRingSide,
        keychainRingEmbeddingDepth,
        keychainRingSize,
        keychainRingThickness,
      };
      baselineInitializedRef.current = true;

      if (customization._id) {
        if (onDraftSaved) {
          onDraftSaved(customization._id);
        }
        if (onCustomizationUpdated) {
          onCustomizationUpdated(customization._id);
        }
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
    }
  }, [elements, productId, backgroundColor, backgroundDepth, backgroundForm, backgroundEnabled, backgroundWidth, backgroundHeight, backgroundBorderRadius, keychainRingEnabled, keychainRingSide, keychainRingEmbeddingDepth, draftCustomizationId, onDraftSaved, onCustomizationUpdated]);

  const handleSave = useCallback(async () => {
    if (elements.length === 0) {
      return;
    }

    setSaving(true);
    try {
      // Capture screenshot from current camera view
      let previewImageDataURL: string | undefined;
      try {
        if (canvasRef.current && "captureScreenshot" in canvasRef.current) {
          previewImageDataURL = await (canvasRef.current as any).captureScreenshot();
        }
      } catch (error: any) {
        console.error("Error capturing screenshot:", error);
        // Continue saving even if screenshot fails
      }

      const customizationData = {
        productId,
        customizationType: "3d-print" as const,
        elements: elements.map((el) => ({
          type: el.type,
          content: el.content,
          position: el.position,
          size: el.size,
          style: el.style,
          zIndex: el.zIndex,
        })),
        backgroundColor,
        threeDData: {
          backgroundDepth,
          backgroundForm,
          enabled: backgroundEnabled,
          width: backgroundWidth,
          height: backgroundHeight,
          borderRadius: backgroundBorderRadius,
          keychainRingEnabled,
          keychainRingSide,
          keychainRingEmbeddingDepth,
          keychainRingSize,
          keychainRingThickness,
        },
        previewImageDataURL: previewImageDataURL, // Send base64 data URL for backend to save
        isDraft: true,
      };

      let customization;
      if (draftCustomizationId) {
        customization = await productCustomizationApi.update(draftCustomizationId, customizationData);
      } else {
        customization = await productCustomizationApi.create(customizationData);
      }

      if (customization._id) {
        baselineStateRef.current = {
          elements: elements.map(({ id, ...el }) => el),
          backgroundColor,
          backgroundDepth,
          backgroundForm,
          keychainRingEnabled,
          keychainRingSide,
          keychainRingEmbeddingDepth,
          keychainRingSize,
          keychainRingThickness,
        };
        baselineInitializedRef.current = true;

        if (onSave) {
          onSave(customization._id);
        }
        if (onDraftSaved) {
          onDraftSaved(customization._id);
        }
        if (onCustomizationUpdated) {
          onCustomizationUpdated(customization._id);
        }
      }
    } catch (error: any) {
      console.error("Error saving customization:", error);
      alert(`Failed to save customization: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [elements, productId, backgroundColor, backgroundDepth, backgroundForm, backgroundEnabled, backgroundWidth, backgroundHeight, backgroundBorderRadius, keychainRingEnabled, keychainRingSide, keychainRingEmbeddingDepth, keychainRingSize, keychainRingThickness, draftCustomizationId, onSave, onDraftSaved, onCustomizationUpdated]);

  const handleCancel = useCallback(() => {
    if (!baselineStateRef.current) {
      if (onCancel) {
        onCancel();
      }
      return;
    }

    const baseline = baselineStateRef.current;

    const hasChanges = 
      elements.length !== baseline.elements.length ||
      elements.some((el, idx) => {
        const baselineEl = baseline.elements[idx];
        if (!baselineEl) return true;
        return (
          el.type !== baselineEl.type ||
          el.content !== baselineEl.content ||
          Math.abs(el.position.x - baselineEl.position.x) > 0.1 ||
          Math.abs(el.position.y - baselineEl.position.y) > 0.1 ||
          Math.abs(el.size.width - baselineEl.size.width) > 0.1 ||
          Math.abs(el.size.height - baselineEl.size.height) > 0.1 ||
          JSON.stringify(el.style) !== JSON.stringify(baselineEl.style) ||
          el.zIndex !== baselineEl.zIndex
        );
      }) ||
      backgroundColor !== baseline.backgroundColor ||
      backgroundDepth !== baseline.backgroundDepth ||
      backgroundForm !== baseline.backgroundForm ||
      Math.abs((keychainRingEmbeddingDepth ?? 0.5) - baseline.keychainRingEmbeddingDepth) > 0.01 ||
      Math.abs((keychainRingSize ?? 1.0) - baseline.keychainRingSize) > 0.01 ||
      Math.abs((keychainRingThickness ?? 1.0) - baseline.keychainRingThickness) > 0.01;

    if (hasChanges) {
      setShowCloseConfirm(true);
    } else {
      if (onCancel) {
        onCancel();
      }
    }
  }, [elements, backgroundColor, backgroundDepth, backgroundForm, keychainRingEmbeddingDepth, keychainRingSize, keychainRingThickness, onCancel]);

  const handleSaveAndClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      await saveDraft();
    }
    setShowCloseConfirm(false);
    if (onCancel) {
      onCancel();
    }
  }, [hasUnsavedChanges, saveDraft, onCancel]);

  const handleCloseWithoutSaving = useCallback(() => {
    setShowCloseConfirm(false);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const selectedElementData = elements.find((el) => el.id === selectedElement);

  const handleElementSelect = (id: string | null) => {
    setSelectedElement(id);
    setSelectedCanvas(false);
    // Switch to components tab to show properties panel when element is selected
    if (id !== null) {
      setActiveTab("components");
    }
  };

  const handleCanvasSelect = () => {
    setSelectedCanvas(true);
    setSelectedElement(null);
  };

  const handleElementDoubleClick = useCallback((id: string) => {
    setSelectedElement(id);
    setSelectedCanvas(false);
    setTimeout(() => {
      if (propertiesPanelRef.current) {
        propertiesPanelRef.current.focusTextInput();
      }
    }, 100);
  }, []);

  // Auto-select canvas layer on component mount
  useEffect(() => {
    setSelectedCanvas(true);
    setSelectedElement(null);
  }, []);

  // Export as 3D model (STL format for Blender)
  const handleExport3D = useCallback(async () => {
    if (elements.length === 0) {
      alert("No design to export");
      return;
    }

    setExporting(true);
    try {
      // Get the canvas component ref and call export method
      const canvasElement = canvasRef.current;
      if (canvasElement && "export3DModel" in canvasElement) {
        await (canvasElement as any).export3DModel();
      } else {
        alert("3D export functionality is not available");
      }
    } catch (error: any) {
      console.error("Error exporting 3D model:", error);
      alert(`Failed to export 3D model: ${error.message || "Unknown error"}`);
    } finally {
      setExporting(false);
    }
  }, [elements]);

  return (
    <>
      <Modal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        title="Unsaved Changes"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleCloseWithoutSaving}>
              Close Without Saving
            </Button>
            <Button variant="primary" onClick={handleSaveAndClose}>
              Save and Close
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          You have unsaved changes to your design. If you close now, your changes will be lost.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Would you like to save your design before closing?
        </p>
      </Modal>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="fixed inset-0 flex bg-gray-100 z-50">
          {/* Left Sidebar - Component Palette and Layers */}
          <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab("components")}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  activeTab === "components"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Components
              </button>
              <button
                onClick={() => setActiveTab("layers")}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  activeTab === "layers"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Layers
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === "components" ? (
                <div className="h-full flex flex-col">
                  <div className="pb-2 overflow-y-auto">
                    <ComponentPalette 
                      onAddElement={addElement}
                      availableShapes={[]}
                      textOnly={true}
                    />
                  </div>
                  {/* Properties Panel */}
                  {(selectedElement || selectedCanvas) && (
                    <>
                      <div className="border-t border-gray-200"></div>
                      <div className="flex-1 overflow-y-auto min-h-0">
                        <ElementPropertiesPanel
                          ref={propertiesPanelRef}
                          element={selectedElementData}
                          isCanvasSelected={selectedCanvas}
                          onUpdate={selectedElementData ? (updates: Partial<DesignElement>) => updateElement(selectedElementData.id, updates) : undefined}
                          onDelete={selectedElementData ? () => deleteElement(selectedElementData.id) : undefined}
                          backgroundColor={backgroundColor}
                          onBackgroundColorChange={setBackgroundColor}
                          backgroundDepth={backgroundDepth}
                          onBackgroundDepthChange={setBackgroundDepth}
                          backgroundForm={backgroundForm}
                          onBackgroundFormChange={setBackgroundForm}
                          backgroundEnabled={backgroundEnabled}
                          onBackgroundEnabledChange={setBackgroundEnabled}
                          backgroundWidth={backgroundWidth}
                          onBackgroundWidthChange={setBackgroundWidth}
                          backgroundHeight={backgroundHeight}
                          onBackgroundHeightChange={setBackgroundHeight}
                          backgroundBorderRadius={backgroundBorderRadius}
                          onBackgroundBorderRadiusChange={setBackgroundBorderRadius}
                          keychainRingEnabled={keychainRingEnabled}
                          onKeychainRingEnabledChange={setKeychainRingEnabled}
                          keychainRingSide={keychainRingSide}
                          onKeychainRingSideChange={setKeychainRingSide}
                          keychainRingEmbeddingDepth={keychainRingEmbeddingDepth}
                          onKeychainRingEmbeddingDepthChange={setKeychainRingEmbeddingDepth}
                          keychainRingSize={keychainRingSize}
                          onKeychainRingSizeChange={setKeychainRingSize}
                          keychainRingThickness={keychainRingThickness}
                          onKeychainRingThicknessChange={setKeychainRingThickness}
                          is3D={true}
                          availableColors={availableColors}
                        />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="h-full overflow-y-auto">
                  <LayerTree
                    elements={elements}
                    selectedElement={selectedElement}
                    onSelectElement={handleElementSelect}
                    onDeleteElement={deleteElement}
                    onUpdateElement={updateElement}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Main Canvas Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heading size="lg">3D Print Designer</Heading>
                {hasUnsavedChanges && (
                  <FaExclamationCircle 
                    className="w-4 h-4 text-amber-500" 
                    title="You have unsaved changes"
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedElement(null);
                    setSelectedCanvas(true);
                  }}
                  className={`flex items-center gap-2 ${selectedCanvas ? "bg-blue-50 border-blue-500" : ""}`}
                  title="Select canvas to edit background properties"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Canvas Properties
                </Button>
                {(user?.role === "admin" || user?.role === "store_admin" || user?.role === "co_admin") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExport3D}
                    disabled={elements.length === 0 || exporting}
                    className="flex items-center gap-2"
                  >
                    <FaCube className="w-4 h-4" />
                    {exporting ? "Exporting..." : "Export 3D Model"}
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={elements.length === 0 || saving}
                  className={`flex items-center gap-2 ${hasUnsavedChanges ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                >
                  <FaSave className="w-4 h-4" />
                  {saving ? "Saving..." : "Save Design"}
                  {hasUnsavedChanges && !saving && (
                    <FaExclamationCircle className="w-3 h-3 ml-1" />
                  )}
                </Button>
                {onCancel && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    className="flex items-center gap-2"
                  >
                    <FaTimes className="w-4 h-4" />
                    Close
                  </Button>
                )}
              </div>
            </div>

            {/* 3D Canvas */}
            <div className="flex-1 overflow-auto bg-gray-50 p-8 flex items-center justify-center">
              <div className="w-full h-full">
                <ThreeDDesignCanvas
                  ref={canvasRef}
                  backgroundColor={backgroundColor}
                  backgroundDepth={backgroundDepth}
                  backgroundForm={backgroundForm}
                  backgroundEnabled={backgroundEnabled}
                  backgroundWidth={backgroundWidth}
                  backgroundHeight={backgroundHeight}
                  backgroundBorderRadius={backgroundBorderRadius}
                  keychainRingEnabled={keychainRingEnabled}
                  keychainRingSide={keychainRingSide}
                  keychainRingEmbeddingDepth={keychainRingEmbeddingDepth}
                  keychainRingSize={keychainRingSize}
                  keychainRingThickness={keychainRingThickness}
                  elements={elements}
                  selectedElement={selectedElement}
                  selectedCanvas={selectedCanvas}
                  onElementSelect={handleElementSelect}
                  onCanvasSelect={handleCanvasSelect}
                  onElementUpdate={updateElement}
                  onElementDelete={deleteElement}
                  onElementDoubleClick={handleElementDoubleClick}
                />
              </div>
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeDragId ? (
              <div className="bg-blue-500 text-white p-2 rounded shadow-lg">
                Dragging...
              </div>
            ) : null}
          </DragOverlay>
        </div>
      </DndContext>
    </>
  );
};

export default ThreeDPrintDesigner;

